import LinearProgress from "@mui/material/LinearProgress"
import { useRouter } from "next/router"
import { FC, useCallback, useEffect, useState } from "react"

import {
  AdvancedForm,
  AdvancedFormStepProps,
} from "src/components/common/advancedForm"

import { create } from "src/helpers/bem"
import { formatFormData } from "src/helpers/form"
import { getFilesById, postBlob } from "src/queriesXRM/file"
import { getForm, postForm } from "src/queriesXRM/form"
import { loadBookings } from "src/queriesXRM/salesorder"
import {
  mergeBooking,
  mergeBookingDetail,
  mergeDetailFormField,
  setDetailActiveStep,
  useBooking,
} from "src/states/bookingDetail"
import { useLocale } from "src/states/locale"
import { addNotification } from "src/states/notifications"
import {
  getSalesorder,
  mergeSalesorderField,
  mergeSalesorderFieldById,
  mergeSalesorderFormById,
  mergeSalesorderStepById,
  resetSalesorder,
  setSalesorderActiveStep,
  setSalesorderSteps,
  useSalesorder,
} from "src/states/salesorder"

import { TranslationMessages } from "src/translations"
import {
  xRMApiField,
  xRMApiFile,
  xRMApiSalesorderPhase,
  xRMApiStep,
} from "src/types/xRM"

import styles from "./MainExhibitorBooking.module.scss"

const bem = create(styles, "MainExhibitorBooking")

export type MainExhibitorBookingProps = {
  messages: TranslationMessages
}

export const MainExhibitorBooking: FC<MainExhibitorBookingProps> = ({
  messages,
}) => {
  const [loading, setLoading] = useState(false)
  const formTranslations = messages.pages.standBooking
  const router = useRouter()
  const locale = useLocale()
  const { detail, editDraft, booking } = useBooking()
  const { salesorderId, activeStep, steps } = useSalesorder()

  const loadForm = useCallback(async () => {
    const data = await getSalesorder()
    setLoading(true)
    const form = await getForm("Main")

    if (data.steps.length === 0) {
      setSalesorderSteps(form)
    } else {
      form.map((step: xRMApiStep) => {
        mergeSalesorderStepById(
          {
            id: step.id,
            position: step.position,
            label: step.label,
            dependencies: step.dependencies,
          },
          step.id,
        )
        return step?.forms?.map((form) => {
          mergeSalesorderFormById(
            {
              id: form.id,
              position: form.position,
              label: form.label,
              helperText: form.helperText,
              fields: form.fields,
            },
            step.id,
            form.id,
          )
          return form?.fields?.map(async (field) => {
            let Field = field
            delete Field.conditions
            delete Field.value
            if (Field.type === "file") {
              const defaultFiles = await getFilesById(
                "Attachment",
                salesorderId,
              )
              const filteredDefaultFiles = defaultFiles?.filter(
                (f: xRMApiFile) => f.formControl === "",
              ) // Before filtered name of field
              Field = {
                ...Field,
                files: filteredDefaultFiles,
              } as xRMApiField
            }
            return mergeSalesorderFieldById(
              {
                ...Field,
              },
              step.id,
              form.id,
              field.id,
            )
          })
        })
      })
    }
    setLoading(false)
  }, [salesorderId])

  useEffect(() => {
    setSalesorderActiveStep(0)
  }, [])

  useEffect(() => {
    loadForm()
  }, [loadForm, locale])

  const uploadFiles = async (files: File[], id: string, control: string) => {
    try {
      if (id) {
        const uploadedFiles = [] as xRMApiFile[]
        for (const file of files) {
          const filename = file?.name
          const metadata: { [k: string]: string } = {
            category: "Form",
            control: control,
          }

          const uploadedFile = await postBlob(
            id,
            file,
            filename,
            metadata,
            "Attachment",
          )

          uploadedFiles.push({
            ...uploadedFile,
            name: file?.name,
            size: file?.size?.toString(),
          })
        }
        return uploadedFiles
      }
    } catch (err) {
      console.error(err)

      addNotification({
        autoclose: true,
        variant: "error",
      })
      return
    }
  }

  const onSendForm = async (phase?: xRMApiSalesorderPhase | "NewDraft") => {
    setLoading(true)
    const xrmPhase = phase === "NewDraft" ? "Draft" : phase
    const data = await formatFormData(
      "Main",
      editDraft ? detail.activeStep : activeStep,
      (editDraft ? detail.steps : steps) as ReadonlyArray<xRMApiStep>,
      undefined,
      // If a request does not contain an ID, a new record is created, whereas
      // a request that includes an ID overwrites any existing records.
      // Therefore, clients must only send an ID when editing an existing draft
      // or converting it into a booking, but not when creating a new record.
      editDraft && (phase === "Draft" || phase === undefined)
        ? booking?.id
        : undefined,
      xrmPhase,
    )
    let redirectURL =
      locale === "de" ? "/de/buchungsdetails" : "/en/booking-details"

    try {
      const files = steps
        .flatMap((step) => step.forms)
        .flatMap((form) => form?.fields)
        .filter((field) => field?.type === "file")

      // Upload the form data without any files.
      const salesorder = await postForm(data, xrmPhase)

      for (const f of files) {
        if (!f || !Array.isArray(f.value) || !(f.value[0] instanceof File)) {
          continue
        }

        // Upload the files, using the ID from the response to associate them
        // with the created booking entity.
        await uploadFiles(f.value as File[], salesorder.id, f.field!)
      }

      mergeBooking({ id: salesorder.id })
      mergeBookingDetail({ editDraft: false })

      await loadBookings(locale)
      router.push(redirectURL)

      setLoading(false)
      resetSalesorder()
      loadForm()
      return salesorder
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LinearProgress color="primary" />
  }

  return (
    <AdvancedForm
      stepLabelsClickable
      validateMinimumStandArea
      activeStep={editDraft ? detail.activeStep : activeStep}
      className={bem()}
      data={(editDraft ? detail.steps : steps) as AdvancedFormStepProps[]}
      formMessages={formTranslations}
      handleMergeField={editDraft ? mergeDetailFormField : mergeSalesorderField}
      id={editDraft ? (booking?.id ?? "") : salesorderId}
      messages={messages}
      buttons={{
        backButtonLabel: formTranslations.buttons.back,
        nextButtonLabel: formTranslations.buttons.next,
        draftButtonLabel: editDraft
          ? formTranslations.buttons.draft
          : undefined,
        finishButtonLabel: formTranslations.buttons.finish,
        saveNewDraftButtonLabel: formTranslations.buttons.saveNewDraft,
      }}
      handleChangeActiveStep={
        editDraft ? setDetailActiveStep : setSalesorderActiveStep
      }
      onSendForm={onSendForm}
    />
  )
}
